import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { ArrowLeft, BookmarkPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/useAuth'
import type { ClientEngagementDocument, ProjectBoardColumnId } from '@/types'
import { BoardView } from './BoardView'
import { BoardModal } from './BoardModal'
import { TaskDialog } from './TaskDialog'
import { SaveAsTemplateDialog } from './SaveAsTemplateDialog'
import { OverviewReconcileBanner } from './OverviewReconcileBanner'
import { useProjectBoard } from '../hooks/useProjectBoard'
import { useBoardStack } from '../hooks/useBoardStack'
import { useCompanyMembers } from '../hooks/useCompanyMembers'
import { withErrorCode } from '@/lib/errors'

interface BoardScreenProps {
  rootBoardId: string
  currentUserId: string
  engagementLineItems?: ClientEngagementDocument['lineItems']
  showBackLink?: boolean
}

export function BoardScreen({
  rootBoardId,
  currentUserId,
  engagementLineItems,
  showBackLink = true,
}: BoardScreenProps) {
  const { user } = useAuth()
  const { board, tasks, loading, error, reload } = useProjectBoard(rootBoardId)
  const { stack, push, pop } = useBoardStack(rootBoardId)
  const { members } = useCompanyMembers()
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [createTaskColumnId, setCreateTaskColumnId] = useState<string | null>(null)
  const [saveOpen, setSaveOpen] = useState(false)
  // Track which task each pushed boardId was opened from (for shared-layout animation)
  const [parentTaskByBoard, setParentTaskByBoard] = useState<Map<string, string>>(
    () => new Map()
  )

  const selectedTask = selectedTaskId
    ? tasks.find((t) => t.id === selectedTaskId) ?? null
    : null

  const currentUser = useMemo(
    () => ({ uid: user?.uid ?? currentUserId, displayName: user?.displayName ?? null }),
    [user, currentUserId]
  )

  const nextOrderForColumn = useCallback(
    (columnId: string) => {
      const orders = tasks
        .filter((t) => t.columnId === columnId)
        .map((t) => t.order ?? 0)
      return orders.length > 0 ? Math.max(...orders) + 1 : 0
    },
    [tasks]
  )

  const handleDrillIn = useCallback(
    (childBoardId: string, parentTaskId: string) => {
      setParentTaskByBoard((prev) => {
        if (prev.get(childBoardId) === parentTaskId) return prev
        const next = new Map(prev)
        next.set(childBoardId, parentTaskId)
        return next
      })
      push(childBoardId)
    },
    [push]
  )

  const handleCreateTaskInColumn = useCallback((columnId: string) => {
    setCreateTaskColumnId(columnId)
  }, [])

  const descendants = stack.slice(1)

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-4 border-b px-6 py-4">
        <div className="min-w-0">
          {showBackLink && (
            <Link to="/admin/client-services/engagements">
              <Button variant="ghost" size="sm" className="-ml-2 mb-1">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to engagements
              </Button>
            </Link>
          )}
          <h1 className="text-xl font-semibold truncate">
            {board?.title ?? 'Project board'}
          </h1>
        </div>
        {board && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSaveOpen(true)}
            >
              <BookmarkPlus className="mr-2 h-4 w-4" />
              Save as template
            </Button>
          </div>
        )}
      </div>

      <SaveAsTemplateDialog
        open={saveOpen}
        boardId={rootBoardId}
        defaultName={board?.title}
        onClose={() => setSaveOpen(false)}
      />

      <div className="flex-1 overflow-auto p-4">
        {loading && (
          <div className="text-sm text-muted-foreground">Loading board…</div>
        )}
        {!loading && error && (
          <div className="text-sm text-destructive">{error.message}</div>
        )}
        {!loading && !error && !board && (
          <div className="text-sm text-muted-foreground">
            {withErrorCode('Board not found.', 'project-board/not-found')}
          </div>
        )}
        {!loading && !error && board?.meta?.isOverview && (
          <OverviewReconcileBanner
            boardId={rootBoardId}
            knownServiceIds={board.meta.serviceIds ?? []}
            engagementLineItems={engagementLineItems}
            onReconciled={async () => {
              await reload()
            }}
          />
        )}
        {!loading && !error && board && (
          <BoardView
            boardId={rootBoardId}
            board={board}
            tasks={tasks}
            currentUserId={currentUserId}
            onReload={reload}
            onOpenTask={(taskId) => setSelectedTaskId(taskId)}
            onCreateTaskInColumn={handleCreateTaskInColumn}
          />
        )}
      </div>

      {/* Edit-mode TaskDialog */}
      {board && selectedTask && (
        <TaskDialog
          open={selectedTaskId !== null}
          mode="edit"
          boardId={rootBoardId}
          board={board}
          members={members}
          currentUser={currentUser}
          task={selectedTask}
          onClose={() => setSelectedTaskId(null)}
          onSaved={reload}
          onDrillIn={(childBoardId) => {
            const parentId = selectedTaskId ?? ''
            setSelectedTaskId(null)
            handleDrillIn(childBoardId, parentId)
          }}
        />
      )}

      {/* Create-mode TaskDialog */}
      {board && createTaskColumnId && (
        <TaskDialog
          open={createTaskColumnId !== null}
          mode="create"
          boardId={rootBoardId}
          board={board}
          members={members}
          currentUser={currentUser}
          initialColumnId={createTaskColumnId as ProjectBoardColumnId}
          initialOrder={nextOrderForColumn(createTaskColumnId)}
          onClose={() => setCreateTaskColumnId(null)}
          onSaved={reload}
        />
      )}

      <AnimatePresence>
        {descendants.map((descendantBoardId, idx) => {
          const parentTaskId = parentTaskByBoard.get(descendantBoardId) ?? ''
          const breadcrumb = [
            board?.title ?? 'Root board',
            ...Array(idx + 1).fill('Sub-board'),
          ]
          return (
            <BoardModal
              key={descendantBoardId}
              boardId={descendantBoardId}
              parentTaskId={parentTaskId}
              breadcrumb={breadcrumb}
              currentUserId={currentUserId}
              onClose={pop}
              onDrillIn={(childBoardId) => handleDrillIn(childBoardId, '')}
            />
          )
        })}
      </AnimatePresence>
    </div>
  )
}
